// Audio Device Selector Component
// Allows users to choose which audio input device to use for live mode

import React, { useState, useEffect } from 'react';

const AudioDeviceSelector = ({ audioDetection, textColor, onDeviceChange }) => {
    const [devices, setDevices] = useState([]);
    const [selectedDevice, setSelectedDevice] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Load available audio input devices
    const loadDevices = React.useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const availableDevices = await audioDetection.getAvailableDevices();
            setDevices(availableDevices);
            
            // Set currently selected device
            if (audioDetection.currentDeviceId) {
                setSelectedDevice(audioDetection.currentDeviceId);
            } else if (availableDevices.length > 0) {
                setSelectedDevice(availableDevices[0].deviceId);
            }
        } catch (error) {
            console.error('Error loading devices:', error);
            setError('Failed to load audio devices. Please check permissions.');
        } finally {
            setIsLoading(false);
        }
    }, [audioDetection]);

    // Load available devices when component mounts or audioDetection changes
    useEffect(() => {
        if (audioDetection) {
            loadDevices();
        }
        
        // Add listener for device changes (devices being plugged/unplugged)
        const handleDeviceChange = () => {
            console.log('Device change detected, re-enumerating devices...');
            if (audioDetection) {
                loadDevices();
            }
        };
        
        if (navigator.mediaDevices && navigator.mediaDevices.addEventListener) {
            navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
        }
        
        return () => {
            if (navigator.mediaDevices && navigator.mediaDevices.removeEventListener) {
                navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
            }
        };
    }, [audioDetection, loadDevices]);

    // Handle device selection change
    const handleDeviceChange = async (e) => {
        const newDeviceId = e.target.value;
        setSelectedDevice(newDeviceId);
        setError(null);
        
        if (audioDetection) {
            try {
                await audioDetection.switchDevice(newDeviceId);
                if (onDeviceChange) {
                    onDeviceChange(newDeviceId);
                }
            } catch (error) {
                console.error('Error switching device:', error);
                setError('Failed to switch audio device. Please try another device.');
            }
        }
    };

    if (!audioDetection || devices.length === 0) {
        return null;
    }

    return (
        <div style={{
            marginTop: '10px',
            padding: '8px',
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '4px'
        }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '5px'
            }}>
                <label 
                    htmlFor="audioDeviceSelect" 
                    style={{ 
                        fontSize: '12px', 
                        color: textColor,
                        opacity: 0.8
                    }}
                >
                    Audio Input:
                </label>
                <button
                    onClick={loadDevices}
                    disabled={isLoading}
                    style={{
                        padding: '3px 8px',
                        fontSize: '11px',
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        border: '1px solid rgba(0, 0, 0, 0.3)',
                        borderRadius: '3px',
                        cursor: isLoading ? 'wait' : 'pointer',
                        opacity: isLoading ? 0.6 : 1
                    }}
                    title="Refresh device list"
                >
                    🔄
                </button>
            </div>
            <select
                id="audioDeviceSelect"
                value={selectedDevice}
                onChange={handleDeviceChange}
                disabled={isLoading}
                style={{
                    width: '100%',
                    padding: '5px',
                    fontSize: '12px',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    border: '1px solid rgba(0, 0, 0, 0.3)',
                    borderRadius: '3px',
                    cursor: isLoading ? 'wait' : 'pointer'
                }}
            >
                {devices.map((device) => (
                    <option key={device.deviceId} value={device.deviceId}>
                        {device.label}
                    </option>
                ))}
            </select>
            {error && (
                <div style={{
                    marginTop: '5px',
                    padding: '5px',
                    fontSize: '11px',
                    color: '#ff4444',
                    backgroundColor: 'rgba(255, 68, 68, 0.1)',
                    borderRadius: '3px'
                }}>
                    {error}
                </div>
            )}
        </div>
    );
};

export default AudioDeviceSelector;
